import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { DialogActions, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import { isAxiosError } from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import SendIcon14pxSvg from "../../../assets/images/icons/SendIcon14pxSvg";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  ErrorCircleButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog, StyledDataGrid } from "../../../components/StyledComponents";
import { useLoading } from "../../../context/LoadingContext";
import { EmailRegex, PersonalIDRegex } from "../../../utils/regex/Regex";
import { checkValid } from "../../../axios/Account/AccountApi";
import CustomSelectNew from "../../../components/CustomSelectNew";
import { useGetGeneralPolicyUserRole } from "../../../hooks/useGeneralService";
import { inviteEmail } from "../../../axios/Invitation/InvitationApi";
import CustomPagination from "../CustomPagination";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";

const InviteNewUserDialog = ({ defaultValue, onClose, onSuccess }) => {
  const [show, hide] = useLoading();
  const { t } = useTranslation();
  const [listInvite, setListInvite] = useState([]);
  const [formData, setFormData] = useState({
    email: defaultValue || "",
    citizenNumber: "",
    permissionId: null,  // dùng để push vào listInvite
    roleName: "",        // chỉ dùng cho UI select
  });
  const [validationMessages, setValidationMessages] = useState({
    email: "",
    citizenNumber: "",
  });
  const invalid = useMemo(() => listInvite.length === 0, [listInvite]);
  const { data: userRoles } = useGetGeneralPolicyUserRole();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [pageSize, setPageSize] = useState(50);

  const [selectWidth, setSelectWidth] = useState(130);
  const selectRef = useRef(null);

  useEffect(() => {
    if (selectRef.current) {
      const w = selectRef.current.offsetWidth;
      setSelectWidth(w + 10); // +10 cho thoáng
    }
  }, [formData.roleName, userRoles]);


  // Map roleName -> label i18n
  const roleLabelMap = {
    EMPLOYEE: t("roles2.Employee"),
    AUDITOR: t("roles2.Auditor"),
    ADMIN: t("roles2.Admin"),
    SUBCRIPTION: t("roles2.SubscriptionAdmin"),
  };

  // Set mặc định EMPLOYEE nếu có
  useEffect(() => {
    if (userRoles && userRoles.length > 0) {
      const employee = userRoles.find((r) => r.roleName === "EMPLOYEE") || userRoles[0];
      setFormData((prev) => ({
        ...prev,
        permissionId: employee?.userRoleId ?? null, // <-- dùng userRoleId làm permissionId
        roleName: employee?.roleName ?? "",
      }));
    }
  }, [userRoles]);

  const handleCloseDialog = () => onClose?.();

  const updateFormDataAndValidate = (name, value) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (name === "email") {
      setValidationMessages((prev) => ({
        ...prev,
        email: value
          ? EmailRegex.test(value)
            ? ""
            : "validation.email-invalid-format"
          : "", // ❌ bỏ required khi đang nhập
      }));
    }

    if (name === "citizenNumber") {
      setValidationMessages((prev) => ({
        ...prev,
        citizenNumber: value
          ? PersonalIDRegex.test(value)
            ? ""
            : "validation.invalid-personal-id"
          : "validation.invalid-personal-id",
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // citizenNumber chỉ cho nhập số
    const nextValue =
      name === "citizenNumber" ? value.replace(/\D/g, "") : value;
    updateFormDataAndValidate(name, nextValue);
  };

  const getRoleLabelByPermissionId = (permissionId) => {
    if (!userRoles || userRoles.length === 0) return "";
    const found = userRoles.find((r) => r.userRoleId === permissionId);
    if (!found) return "";
    return roleLabelMap[found.roleName] ?? found.roleName;
  };

  const handleAddEmail = async () => {
    const { email, citizenNumber, permissionId } = formData;

    try {
      show();

      const response = await checkValid(
        citizenNumber,
        email,
        localStorage.getItem("i18nextLngId")
      );

      // TH thành công (responseCode = 0)
      if (response && response.data && response.data.responseCode === 0) {
        setListInvite((prev) => [
          { citizenNumber, email, permissionId },
          ...prev,
        ]);

        setFormData((prev) => ({
          ...prev,
          email: "",
          citizenNumber: "",
        }));
        setValidationMessages({ email: "", citizenNumber: "" });
        return;
      }

      // TH API trả lỗi nhưng vẫn 200
      toast.error(response?.data?.message || t("common.error"));
    } catch (error) {
      if (isAxiosError(error)) {
        const rawData = error.response && error.response.data;
        let code;
        let msg;

        // 🔹 TH 1: server trả string kiểu "400 Bad Request: "{...}""
        if (typeof rawData === "string") {
          try {
            const start = rawData.indexOf("{");
            const end = rawData.lastIndexOf("}");

            if (start !== -1 && end !== -1 && end > start) {
              const jsonStr = rawData.substring(start, end + 1);
              const parsed = JSON.parse(jsonStr);

              code = parsed.responseCode;
              msg = parsed.message;
            }
          } catch (e) {
            // parse lỗi thì kệ, xuống fallback
          }
        }

        // 🔹 TH 2: server trả object JSON bình thường
        if (!code && rawData && typeof rawData === "object") {
          code = rawData.responseCode;
          msg = rawData.message;
        }

        // Nếu đã lấy được responseCode từ body
        if (code) {
          if (code === 1049) {
            // tuỳ bạn dùng msg hay i18n
            toast.error(msg || "Không tìm thấy tài khoản");
          } else {
            toast.error(msg || t("common.error"));
          }
          return;
        }

        // Không có responseCode nhưng là 500 -> lỗi server
        if (error.response && error.response.status === 500) {
          toast.error(t("ERRORMSG_1066"));
          return;
        }

        // Fallback chung
        const fallback =
          (typeof rawData === "string" ? rawData : "") ||
          (rawData && rawData.error) ||
          error.message;

        toast.error(fallback || t("common.error"));
      } else {
        toast.error(t("common.error"));
      }
    } finally {
      hide();
    }
  };

  const handleRemoveRow = (rowIndex) => {
    setListInvite((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      show();
      await inviteEmail(listInvite);
      toast.success(t("common.content-079"));
      onSuccess?.();
      handleCloseDialog();
    } catch (error) {
      if (isAxiosError(error) && error.response && error.response.status !== 401) {
        toast.error(t(error?.response?.data) || t("common.error"));
      } else {
        toast.error(t('common.network-timeout'));
      }
    } finally {
      hide();
    }
  };

  // rows cho StyledDataGrid
  const rows = useMemo(
    () =>
      listInvite.map((item, idx) => ({
        id: idx, // dùng index làm id (ổn vì listInvite chỉ thay đổi local)
        citizenNumber: item.citizenNumber,
        email: item.email,
        permissionId: item.permissionId,
      })),
    [listInvite]
  );

  // columns cho StyledDataGrid
  const columns = useMemo(
    () => [
      {
        field: "citizenNumber",
        headerName: t("common.personal-id"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "email",
        headerName: t("common.email"),
        flex: 1.2,
        minWidth: 220,
      },
      {
        field: "permissionId",
        headerName: t("common.admin"),
        flex: 1,
        minWidth: 180,
        renderCell: ({ value }) => getRoleLabelByPermissionId(value),
      },
      {
        field: "options",
        headerName: t("common.options"),
        sortable: false,
        filterable: false,
        width: 100,
        renderCell: (params) => (
          <div className="flex gap-2 items-center justify-center w-full mt-2">
            <Tooltip
              title={
                <div>
                  <div>{t('button.delete')}</div>
                </div>
              }
              placement="bottom"
              arrow
            ><ErrorCircleButton onClick={() => handleRemoveRow(params.id)}>
                <DeleteIcon />
              </ErrorCircleButton></Tooltip>
          </div>
        ),
      },
    ],
    [t, userRoles]
  );

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newPageSize) => {
    setRowsPerPage(typeof newPageSize === "number" ? newPageSize : Number(newPageSize));
    setPage(0);
  };

  return (
    <CustomDialog
      open
      keepMounted
      sx={{ ".MuiPaper-root": { width: "800px" } }}
      maxWidth={"xl"}
      onClose={handleCloseDialog}
      transitionDuration={window.__tourIsOpen ? 0 : undefined}
      data-tour="user-management-invite-new-user-dialog"
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("button.invite-new-user")}</p>
          </div>
          <GrayIconButton onClick={handleCloseDialog} className="self-start">
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <p className="pt-5">{t("common.invite-user-to-use-the-system")}.</p>

        {/* Form nhập */}
        <div
          className="flex gap-2 pt-4 w-full items-center"
          data-tour="user-management-invite-new-user-input"
        >
          {/* citizenNumber */}
          <div>
            <Tooltip
              title={formData.citizenNumber ? formData.citizenNumber : ""}
              placement="bottom"
              arrow
              disableHoverListener={!formData.citizenNumber}
              disableFocusListener={!formData.citizenNumber}
              disableTouchListener={!formData.citizenNumber}
            >
              <label className="block text-sm">
                {t("common.personal-id")}
                <span className="text-[#EF4444] ml-1">*</span>
              </label>
              <div className="mt-2 relative flex items-center">
                <input
                  name="citizenNumber"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`form-control relative w-[210px] z-40 h-10 ${!!validationMessages.citizenNumber ? "invalid" : ""
                    }`}
                  value={formData.citizenNumber}
                  onChange={handleInputChange}
                />
              </div>
              {validationMessages.citizenNumber ? (
                <p className="text-[14px] text-red-500 pt-1">
                  {t(validationMessages.citizenNumber)}
                </p>
              ) : validationMessages.email ? (
                <div className="pt-6"></div>
              ) : null}</Tooltip>
          </div>

          {/* email + role select */}
          <div>
            <Tooltip
              title={formData.email ? formData.email : ""}
              placement="bottom"
              arrow
              disableHoverListener={!formData.email || !!validationMessages.email}
              disableFocusListener={!formData.email || !!validationMessages.email}
              disableTouchListener={!formData.email || !!validationMessages.email}
            ><label className="block text-sm ">
                {t("common.email")}
                <span className="text-[#EF4444] ml-1">*</span>
              </label>

              <div className="mt-2 relative flex items-center w-fit">
                <input
                  name="email"
                  className={`form-control relative w-[460px] h-10 z-10 ${!!validationMessages.email ? "invalid" : ""}`}
                  style={{ paddingRight: selectWidth }}
                  value={formData.email}
                  onChange={handleInputChange}
                />

                <div className="absolute right-[6px] z-20" ref={selectRef}>
                  <CustomSelectNew
                    name="roleName"
                    value={formData.roleName}
                    onChange={(valOrEvent) => {
                      const value = valOrEvent?.target ? valOrEvent.target.value : valOrEvent;
                      const role = (userRoles || []).find((r) => r.roleName === value);
                      setFormData((prev) => ({
                        ...prev,
                        roleName: value,
                        permissionId: role?.userRoleId ?? null,
                      }));
                    }}
                    options={userRoles || []}
                    dataType="administratorRights"
                  />
                </div>

              </div>

              {validationMessages.email ? (
                <p className="text-[14px] text-red-500 pt-1">
                  {t(validationMessages.email)}
                </p>
              ) : validationMessages.citizenNumber ? (
                <div className="pt-6"></div>
              ) : null}</Tooltip>

          </div>

          <div>
            <label className="block text-sm mt-2">
              <span className="text-[#EF4444] ml-1"></span>
            </label>
            <ControlpanelPrimaryButton
              disabled={
                !formData.email ||
                !!validationMessages.email ||
                !formData.citizenNumber ||
                !!validationMessages.citizenNumber
              }
              onClick={handleAddEmail}
            >
              {t("button.add")}
            </ControlpanelPrimaryButton>

            {(validationMessages.email || validationMessages.citizenNumber) && (
              <div className="pt-6"></div>
            )}
          </div>
        </div>

        {/* Bảng StyledDataGrid */}
        <p className="uppercase font-semibold py-2">{t("common.employee")}</p>
        <div className="h-[250px]" data-tour="user-management-invite-new-user-table">
          <StyledDataGrid
            sx={{
              height: 250,
              "& .MuiDataGrid-virtualScroller": {
                scrollbarWidth: "thin",
              },
            }}
            rows={rows}
            columns={columns}
            rowCount={listInvite.length}
            paginationMode="client"
            slots={{
              pagination: CustomPagination,
              noRowsOverlay: NoRowOverlay,
              columnsPanel: CustomColumnsPanel,
            }}
            slotProps={{
              pagination: {
                page,
                count: listInvite.length,
                rowsPerPage: pageSize,
                onPageChange: setPage,
                onRowsPerPageChange: setPageSize,
              },
            }}
            paginationModel={{
              page,
              pageSize: rowsPerPage,
            }}
            onPaginationModelChange={(model) => {
              if (model.page !== page) setPage(model.page);
              if (model.pageSize !== rowsPerPage) setRowsPerPage(model.pageSize);
            }}
            disableRowSelectionOnClick
          />
        </div>
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog}>
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            className="!gap-2"
            data-tour="user-management-invite-new-user-send"
            disabled={invalid}
            onClick={handleSubmit}
          >
            <p>{t("button.send")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default InviteNewUserDialog;
